import * as THREE from 'three';

import type { ResourceTypeHandle, Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import { resolveWorkflowStateChain } from '@toolproof-core/lib/_pure/workflow-state-resolution';
import type { MutableMaterializedResource, MutableToolStep, MutableWorkflowState } from '@toolproof-core/lib/_contract/workflow-overlay';

import { deriveFrameBoundingSphere } from '@toolproof-core/visualization/rendering/_pure/frame-bounds';
import type { EntityConstellation } from '@toolproof-core/visualization/_contract/constellation';
import type {
    ConnectorEndpointRef,
    EntityItemParametricState,
} from '@toolproof-core/visualization/_contract/item';
import type { ConstellationKey, ItemKey } from '@toolproof-core/visualization/_contract/key';
import type {
    InterpolatedFramePatch,
    SpaceDerivationFrame,
    TransitionPhase,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { buildBooleanRodState, toBooleanWheelPositions } from '@/adapters/primitive/_pure/derivation/boolean/traceProjection';
import { UNSCALED_PRIMITIVE_CONFIG } from '@/adapters/primitive/_data/config/config';
import { withPrimitiveColorPalette } from '@/adapters/primitive/_pure/config/colorPalette';
import {
    toPrimitiveHubConstellationKey,
    toPrimitivePowerConstellationKey,
    toPrimitiveWheelConnectorAnchorItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import {
    buildPrimitiveRodFrame,
    toPrimitiveRodPlacementBasis,
    toScopedPrimitiveRodKey,
    type PrimitiveRodPlacement,
} from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { toPrimitiveGeometryFramePatch } from '@/adapters/primitive/_pure/derivation/primitiveStepTransitions';
import { derivePrimitiveStepStatesFromOperationInput, toPrimitiveStepWheels } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import {
    buildBooleanPlaceholderWheel,
    buildNaturalPlaceholderWheel,
    buildOperandWheel,
    dimStableWheelRoles,
    toWheelLayout,
    toWheelPosition,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { scaleAnimationDurations } from '@/_pure/animationDurations';
import { GEOMETRY_SCALE } from '@/workflow/_data/config/fragments/geometryScale';
import { ABOVE_ITEM_LABEL } from '@/workflow/_data/config/fragments/labels';
import {
    getToolRingOuterRadius,
} from '@/workflow/_pure/config/derivationConfig';
import { PRESENTATION_DEFAULTS } from '@/workflow-runner/_data/config/fragments/_@shared/presentationDefaults';
import {
    buildToolStepItemKey,
    buildWorkflowStateEntryItemKey,
    parseToolStepConstellationKey,
    parseWorkflowStateEntryConstellationKey,
    parseWorkflowStateEntryItemKey,
} from '@/workflow/_pure/keyCodec';
import { resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import {
    buildBooleanGraphFrame,
    deriveBooleanGraphConfig,
    deriveBooleanGraphPlacement,
    replaceBooleanAlignment,
} from '@/workflow-runner/_pure/booleanOperationGraph';
import { applyWorkflowStateEntryConnectorVisibility } from '@/workflow-runner/_pure/workflowStateEntryConnectorVisibility';
import { rodStepMomentKey } from '@/workflow-runner/_pure/workflowMoment';
import { WORKFLOW_ROD_DIRECTION_CATALOG } from '@/workflow-runner/_pure/workflowDirectionRules';
import type { BooleanAlgebraOperation } from '@/adapters/primitive/_contract/boolean/operation';
import type { NaturalOperation } from '@/adapters/primitive/_contract/natural/arithmetic';
import type { Config as PrimitiveConfig, PrimitivePresentationOperation, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInput } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { OperationPresentationMode } from '@/workflow/_contract/config';
import type { Config } from '@/workflow-runner/_contract/config';
import type { PrimitiveCameraPlanInput } from '@/workflow-runner/_contract/primitiveCameraPlan';
import type { PrimitiveOperationPendingReplayMove } from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshot, TraceSnapshotView } from '@/workflow-runner/_contract/traceSnapshot';

type PrimitiveOperationWorkflowRole =
    | 'AddendOne'
    | 'AddendTwo'
    | 'Sum'
    | 'Minuend'
    | 'Subtrahend'
    | 'Difference'
    | 'Multiplicand'
    | 'Multiplier'
    | 'Product'
    | 'Dividend'
    | 'Divisor'
    | 'Quotient'
    | 'Remainder'
    | 'LessThanSource'
    | 'LessThanTarget'
    | 'LessThanDecision'
    | 'In'
    | 'Out'
    | BooleanWorkflowRole;
type BooleanWorkflowRole = 'OperandOne' | 'OperandTwo' | 'Operand' | 'Result';

type PrimitiveOperationOutputBinding = {
    workflowRole: PrimitiveOperationWorkflowRole;
    wheelRole: PrimitiveWheelState['role'];
};

type NaturalArithmeticDescriptor = {
    descriptorKind: 'arithmetic';
    operation: NaturalOperation;
    title: 'Natural Addition' | 'Natural Subtraction' | 'Natural Multiplication' | 'Natural Division';
    toolHandle: MutableToolStep['toolHandle'];
    inputRoles: readonly [PrimitiveOperationWorkflowRole, PrimitiveOperationWorkflowRole];
    outputBindings: readonly [PrimitiveOperationOutputBinding, ...PrimitiveOperationOutputBinding[]];
    axisOutputRole: PrimitiveOperationWorkflowRole;
    wheelRoleByWorkflowRole: Readonly<Partial<Record<PrimitiveOperationWorkflowRole, PrimitiveWheelState['role']>>>;
    resourceTypeHandleByWorkflowRole: Readonly<Partial<Record<PrimitiveOperationWorkflowRole, ResourceTypeHandle>>>;
};

type IdentityDescriptor = {
    descriptorKind: 'identity';
    operation: Extract<PrimitivePresentationOperation, 'natural-identity' | 'boolean-identity'>;
    title: 'Natural Identity' | 'Boolean Identity';
    toolHandle: MutableToolStep['toolHandle'];
    resourceTypeHandle: ResourceTypeHandle;
    inputRoles: readonly ['In'];
    outputBindings: readonly [{ workflowRole: 'Out'; wheelRole: 'identity-output' }];
    axisOutputRole: 'Out';
    wheelRoleByWorkflowRole: Readonly<{ In: 'identity-input'; Out: 'identity-output' }>;
    resourceTypeHandleByWorkflowRole: Readonly<{ In: ResourceTypeHandle; Out: ResourceTypeHandle }>;
};

type NaturalLessThanDescriptor = {
    descriptorKind: 'natural-less-than';
    operation: 'natural-less-than';
    title: 'Natural Less Than';
    toolHandle: MutableToolStep['toolHandle'];
    inputRoles: readonly ['LessThanSource', 'LessThanTarget'];
    outputBindings: readonly [{ workflowRole: 'LessThanDecision'; wheelRole: 'less-than-decision' }];
    axisOutputRole: 'LessThanDecision';
    wheelRoleByWorkflowRole: Readonly<{
        LessThanSource: 'less-than-source';
        LessThanTarget: 'less-than-target';
        LessThanDecision: 'less-than-decision';
    }>;
    resourceTypeHandleByWorkflowRole: Readonly<{
        LessThanSource: ResourceTypeHandle;
        LessThanTarget: ResourceTypeHandle;
        LessThanDecision: ResourceTypeHandle;
    }>;
};

type BooleanAlgebraDescriptor = {
    descriptorKind: 'boolean-algebra';
    operation: BooleanAlgebraOperation;
    title: string;
    toolHandle: ToolHandle;
    inputRoles: readonly ['Operand'] | readonly ['OperandOne', 'OperandTwo'];
    outputBindings: readonly [{ workflowRole: 'Result'; wheelRole: 'boolean-output' }];
    axisOutputRole: 'Result';
    wheelRoleByWorkflowRole: Readonly<Partial<Record<BooleanWorkflowRole, PrimitiveWheelState['role']>>>;
    resourceTypeHandleByWorkflowRole: Readonly<Partial<Record<BooleanWorkflowRole, ResourceTypeHandle>>>;
};

type PrimitiveOperationRodDescriptor = NaturalArithmeticDescriptor | IdentityDescriptor | NaturalLessThanDescriptor | BooleanAlgebraDescriptor;

const BOOLEAN_OPERATION_BY_TOOL_HANDLE = new Map<ToolHandle, BooleanAlgebraOperation>([
    [CONSTANTS.Handles.Tools.TOOL_LogicalAnd, 'logical-and'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalNAnd, 'logical-nand'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalOr, 'logical-or'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalNOr, 'logical-nor'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalXOr, 'logical-xor'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalXNor, 'logical-xnor'],
    [CONSTANTS.Handles.Tools.TOOL_LogicalNot, 'logical-not'],
]);

const NATURAL_ARITHMETIC_DESCRIPTOR_BY_TEMPLATE_TOOL_HANDLE = new Map<
    ToolHandle,
    NaturalArithmeticDescriptor
>([
    [CONSTANTS.Handles.Tools.TOOL_Add, {
        descriptorKind: 'arithmetic',
        operation: 'addition',
        title: 'Natural Addition',
        toolHandle: CONSTANTS.Handles.Tools.TOOL_NaturalAdd,
        inputRoles: ['AddendOne', 'AddendTwo'],
        outputBindings: [{ workflowRole: 'Sum', wheelRole: 'sum' }],
        axisOutputRole: 'Sum',
        wheelRoleByWorkflowRole: {
            AddendOne: 'addend-one',
            AddendTwo: 'addend-two',
            Sum: 'sum',
        },
        resourceTypeHandleByWorkflowRole: {
            AddendOne: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            AddendTwo: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Sum: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        },
    }],
    [CONSTANTS.Handles.Tools.TOOL_Subtract, {
        descriptorKind: 'arithmetic',
        operation: 'subtraction',
        title: 'Natural Subtraction',
        toolHandle: CONSTANTS.Handles.Tools.TOOL_NaturalSubtract,
        inputRoles: ['Minuend', 'Subtrahend'],
        outputBindings: [{ workflowRole: 'Difference', wheelRole: 'difference' }],
        axisOutputRole: 'Difference',
        wheelRoleByWorkflowRole: {
            Minuend: 'minuend',
            Subtrahend: 'subtrahend',
            Difference: 'difference',
        },
        resourceTypeHandleByWorkflowRole: {
            Minuend: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Subtrahend: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Difference: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        },
    }],
    [CONSTANTS.Handles.Tools.TOOL_Multiply, {
        descriptorKind: 'arithmetic',
        operation: 'multiplication',
        title: 'Natural Multiplication',
        toolHandle: CONSTANTS.Handles.Tools.TOOL_NaturalMultiply,
        inputRoles: ['Multiplier', 'Multiplicand'],
        outputBindings: [{ workflowRole: 'Product', wheelRole: 'product' }],
        axisOutputRole: 'Product',
        wheelRoleByWorkflowRole: {
            Multiplicand: 'multiplicand',
            Multiplier: 'multiplier',
            Product: 'product',
        },
        resourceTypeHandleByWorkflowRole: {
            Multiplicand: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Multiplier: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Product: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        },
    }],
    [CONSTANTS.Handles.Tools.TOOL_Divide, {
        descriptorKind: 'arithmetic',
        operation: 'division',
        title: 'Natural Division',
        toolHandle: CONSTANTS.Handles.Tools.TOOL_NaturalDivide,
        inputRoles: ['Dividend', 'Divisor'],
        outputBindings: [
            { workflowRole: 'Remainder', wheelRole: 'remainder' },
            { workflowRole: 'Quotient', wheelRole: 'quotient' },
        ],
        axisOutputRole: 'Quotient',
        wheelRoleByWorkflowRole: {
            Dividend: 'dividend',
            Divisor: 'divisor',
            Quotient: 'quotient',
            Remainder: 'remainder',
        },
        resourceTypeHandleByWorkflowRole: {
            Dividend: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Divisor: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Quotient: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            Remainder: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        },
    }],
]);

export function resolveRodDescriptor(tool: Tool | undefined): PrimitiveOperationRodDescriptor | undefined {
    const booleanOperation = tool === undefined ? undefined : BOOLEAN_OPERATION_BY_TOOL_HANDLE.get(tool.handle);
    if (booleanOperation !== undefined && tool !== undefined) {
        const unary = booleanOperation === 'logical-not';
        return {
            descriptorKind: 'boolean-algebra',
            operation: booleanOperation,
            title: `Logical ${booleanOperation.slice('logical-'.length).toUpperCase()}`,
            toolHandle: tool.handle,
            inputRoles: unary ? ['Operand'] : ['OperandOne', 'OperandTwo'],
            outputBindings: [{ workflowRole: 'Result', wheelRole: 'boolean-output' }],
            axisOutputRole: 'Result',
            wheelRoleByWorkflowRole: { Operand: 'boolean-input-one', OperandOne: 'boolean-input-one', OperandTwo: 'boolean-input-two', Result: 'boolean-output' },
            resourceTypeHandleByWorkflowRole: {
                Operand: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
                OperandOne: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
                OperandTwo: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
                Result: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
            },
        };
    }
    if (tool?.handle === CONSTANTS.Handles.Tools.TOOL_NaturalLessThan) {
        return {
            descriptorKind: 'natural-less-than',
            operation: 'natural-less-than',
            title: 'Natural Less Than',
            toolHandle: tool.handle,
            inputRoles: ['LessThanSource', 'LessThanTarget'],
            outputBindings: [{ workflowRole: 'LessThanDecision', wheelRole: 'less-than-decision' }],
            axisOutputRole: 'LessThanDecision',
            wheelRoleByWorkflowRole: {
                LessThanSource: 'less-than-source',
                LessThanTarget: 'less-than-target',
                LessThanDecision: 'less-than-decision',
            },
            resourceTypeHandleByWorkflowRole: {
                LessThanSource: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
                LessThanTarget: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
                LessThanDecision: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
            },
        };
    }
    if (tool?.templateToolHandle === undefined) return undefined;
    const arithmetic = NATURAL_ARITHMETIC_DESCRIPTOR_BY_TEMPLATE_TOOL_HANDLE.get(tool.templateToolHandle);
    if (arithmetic !== undefined) return arithmetic;
    if (tool.templateToolHandle !== CONSTANTS.Handles.Tools.TOOL_Identity) return undefined;

    const inputType = tool.roleSpec.inputRoleByName.In?.resourceTypeHandle;
    const outputType = tool.roleSpec.outputRoleByName.Out?.resourceTypeHandle;
    if (inputType !== outputType) return undefined;
    const operation = inputType === CONSTANTS.Handles.ResourceTypes.TYPE_Natural
        ? 'natural-identity'
        : inputType === CONSTANTS.Handles.ResourceTypes.TYPE_Boolean
            ? 'boolean-identity'
            : undefined;
    if (operation === undefined) return undefined;
    return {
        descriptorKind: 'identity',
        operation,
        title: operation === 'natural-identity' ? 'Natural Identity' : 'Boolean Identity',
        toolHandle: tool.handle,
        resourceTypeHandle: inputType,
        inputRoles: ['In'],
        outputBindings: [{ workflowRole: 'Out', wheelRole: 'identity-output' }],
        axisOutputRole: 'Out',
        wheelRoleByWorkflowRole: { In: 'identity-input', Out: 'identity-output' },
        resourceTypeHandleByWorkflowRole: { In: inputType, Out: outputType },
    };
}

const PRIMITIVE_ROD_CONFIG: PrimitiveConfig = UNSCALED_PRIMITIVE_CONFIG;

export function resolvePrimitiveRodConfig(
    config: Config,
    durationMultiplier: number = PRESENTATION_DEFAULTS.animationDurationMultiplier,
): PrimitiveConfig {
    const rootScale = getToolRingOuterRadius(config) / GEOMETRY_SCALE.rootRadius;
    const wheelBandScale = rootScale
        * config.geometryScale.workflowStateEntries.wheels.bandGeometry;

    const placedConfig: PrimitiveConfig = {
        ...PRIMITIVE_ROD_CONFIG,
        ...(config.adapterModeConfig.sceneDirectionCatalogs ? { sceneDirectionCatalogs: {
            visuals: config.adapterModeConfig.sceneDirectionCatalogs.rodVisuals,
            camera: config.adapterModeConfig.sceneDirectionCatalogs.rodCamera,
        } } : {}),
        boolean: {
            ...PRIMITIVE_ROD_CONFIG.boolean,
            display: {
                ...PRIMITIVE_ROD_CONFIG.boolean.display,
                layout: {
                    operandSpreadAngleDegrees: PRIMITIVE_ROD_CONFIG.boolean.display.layout.operandSpreadAngleDegrees,
                    resultChannelDepth: PRIMITIVE_ROD_CONFIG.boolean.display.layout.resultChannelDepth * rootScale,
                    branchHeight: PRIMITIVE_ROD_CONFIG.boolean.display.layout.branchHeight * rootScale,
                },
                track: {
                    ...PRIMITIVE_ROD_CONFIG.boolean.display.track,
                    halfGap: PRIMITIVE_ROD_CONFIG.boolean.display.track.halfGap * rootScale,
                    lipThickness: PRIMITIVE_ROD_CONFIG.boolean.display.track.lipThickness * rootScale,
                    lipHeight: PRIMITIVE_ROD_CONFIG.boolean.display.track.lipHeight * rootScale,
                    floorDepth: PRIMITIVE_ROD_CONFIG.boolean.display.track.floorDepth * rootScale,
                    barHeight: PRIMITIVE_ROD_CONFIG.boolean.display.track.barHeight * rootScale,
                    wheelEndGap: PRIMITIVE_ROD_CONFIG.boolean.display.track.wheelEndGap * rootScale,
                },
                splitMarker: { ...PRIMITIVE_ROD_CONFIG.boolean.display.splitMarker, halfWidth: PRIMITIVE_ROD_CONFIG.boolean.display.splitMarker.halfWidth * rootScale },
            },
        },
        base: {
            ...PRIMITIVE_ROD_CONFIG.base,
            display: {
                ...PRIMITIVE_ROD_CONFIG.base.display,
                wheel: {
                    ...PRIMITIVE_ROD_CONFIG.base.display.wheel,
                    innerRadius: PRIMITIVE_ROD_CONFIG.base.display.wheel.innerRadius * wheelBandScale,
                    ringWidth: PRIMITIVE_ROD_CONFIG.base.display.wheel.ringWidth * wheelBandScale,
                    wheelGap: PRIMITIVE_ROD_CONFIG.base.display.wheel.wheelGap * rootScale,
                    standoffNearOffset: PRIMITIVE_ROD_CONFIG.base.display.wheel.standoffNearOffset * rootScale,
                    standoffFarGap: PRIMITIVE_ROD_CONFIG.base.display.wheel.standoffFarGap * rootScale,
                },
                label: {
                    ...PRIMITIVE_ROD_CONFIG.base.display.label,
                    // Tracks the band scale so the title keeps a constant gap from the wheel rim.
                    wheelTitleRadialOffset: PRIMITIVE_ROD_CONFIG.base.display.label.wheelTitleRadialOffset * wheelBandScale,
                    aboveItemStyleOverride: {
                        ...ABOVE_ITEM_LABEL,
                        fontSize: (ABOVE_ITEM_LABEL.fontSize ?? 0.24) * rootScale,
                        padding: (ABOVE_ITEM_LABEL.padding ?? 0.12) * rootScale,
                    },
                },
                placeholder: {
                    ...PRIMITIVE_ROD_CONFIG.base.display.placeholder,
                    outlineWidth: PRIMITIVE_ROD_CONFIG.base.display.placeholder.outlineWidth * rootScale,
                },
            },
        },
        natural: {
            ...PRIMITIVE_ROD_CONFIG.natural,
            display: {
                ...PRIMITIVE_ROD_CONFIG.natural.display,
                comparison: {
                    ...PRIMITIVE_ROD_CONFIG.natural.display.comparison,
                    naturalLessThanPresentationMode: config.adapterModeConfig.playback.naturalLessThanPresentationMode,
                    rayWidth: PRIMITIVE_ROD_CONFIG.natural.display.comparison.rayWidth * rootScale,
                },
                wheel: {
                    ...PRIMITIVE_ROD_CONFIG.natural.display.wheel,
                    ringGap: PRIMITIVE_ROD_CONFIG.natural.display.wheel.ringGap * wheelBandScale,
                },
                marker: {
                    ...PRIMITIVE_ROD_CONFIG.natural.display.marker,
                    axisRod: {
                        ...PRIMITIVE_ROD_CONFIG.natural.display.marker.axisRod,
                        width: PRIMITIVE_ROD_CONFIG.natural.display.marker.axisRod.width * rootScale,
                    },
                },
            },
        },
    };
    return scaleAnimationDurations(withPrimitiveColorPalette(placedConfig, config.colorPalette), durationMultiplier);
}

type PrimitiveRodProjection = number | boolean;

export type PrimitiveOperationRodResolution =
    | { status: 'unsupported-tool' }
    | { status: 'unresolved-input'; roleName: PrimitiveOperationWorkflowRole }
    | { status: 'invalid-projection'; roleName: PrimitiveOperationWorkflowRole; projection: unknown }
    | { status: 'invalid-operation'; message: string }
    | { status: 'output-mismatch'; roleName: PrimitiveOperationWorkflowRole; expected: PrimitiveRodProjection; actual: PrimitiveRodProjection | null }
    | {
        status: 'ready';
        descriptor: PrimitiveOperationRodDescriptor;
        input: PrimitiveOperationInput;
        steps: readonly PrimitiveStepState[];
        resultByOutputRole: Readonly<Partial<Record<PrimitiveOperationWorkflowRole, PrimitiveRodProjection>>>;
    };

type ReadyPrimitiveOperationRodResolution = Extract<PrimitiveOperationRodResolution, { status: 'ready' }>;

export type ReadyPrimitiveOperationReplay = {
    toolStep: Pick<MutableToolStep, 'stepKey' | 'toolHandle'>;
    resolution: ReadyPrimitiveOperationRodResolution;
};

export function derivePrimitiveOperationReplayEventKind(args: {
    direction: 'forward' | 'reverse';
    operationPresentationMode: OperationPresentationMode;
    toolStep: Pick<MutableToolStep, 'stepKey' | 'toolHandle'> | undefined;
    preWorkflowState: MutableWorkflowState;
    postWorkflowState: MutableWorkflowState;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): 'primitive-operation' | 'none' {
    if (args.direction !== 'forward') return 'none';
    if (args.operationPresentationMode !== 'wheels') return 'none';
    if (!args.toolStep) return 'none';
    return resolvePrimitiveOperationRod({
        toolStep: args.toolStep,
        preWorkflowState: args.preWorkflowState,
        postWorkflowState: args.postWorkflowState,
        primitiveConfig: PRIMITIVE_ROD_CONFIG,
        toolMap: args.toolMap,
    }).status === 'ready'
        ? 'primitive-operation'
        : 'none';
}

function toNaturalProjection(entry: MutableMaterializedResource | null): number | null {
    if (entry?.resourceTypeHandle !== CONSTANTS.Handles.ResourceTypes.TYPE_Natural) return null;
    return typeof entry.projection === 'number'
        && Number.isFinite(entry.projection)
        && Number.isInteger(entry.projection)
        && entry.projection >= 0
        ? entry.projection
        : null;
}

function toPrimitiveRodProjection(
    entry: MutableMaterializedResource | null,
    resourceTypeHandle: ResourceTypeHandle,
): PrimitiveRodProjection | null {
    if (resourceTypeHandle === CONSTANTS.Handles.ResourceTypes.TYPE_Boolean) {
        return entry?.resourceTypeHandle === resourceTypeHandle && typeof entry.projection === 'boolean'
            ? entry.projection
            : null;
    }
    return toNaturalProjection(entry);
}

function resolveInputEntry(args: {
    workflowState: MutableWorkflowState;
    stepKey: MutableToolStep['stepKey'];
    roleName: PrimitiveOperationWorkflowRole;
}): MutableMaterializedResource | null {
    const resolved = resolveWorkflowStateChain(args.workflowState, {
        stepKey: args.stepKey,
        roleName: args.roleName,
    });
    return resolved.status === 'materializedResource' ? resolved.entry : null;
}

function resolveOutputEntry(args: {
    workflowState: MutableWorkflowState;
    stepKey: MutableToolStep['stepKey'];
    roleName: PrimitiveOperationWorkflowRole;
}): MutableMaterializedResource | null {
    const entry = args.workflowState[args.stepKey]?.[args.roleName];
    return entry?.workflowStateEntryKind === 'materializedResource' ? entry : null;
}

function toWheelValue(wheel: PrimitiveWheelState | undefined, radix: number): number {
    if (wheel === undefined) return 0;
    return wheel.powers.reduce(
        (value, power) => value + (wheel.countedThroughDigitByPower?.[power] ?? 0) * (radix ** power),
        0,
    );
}

function toRodWheelValue(wheel: PrimitiveWheelState | undefined, radix: number): PrimitiveRodProjection {
    return wheel?.valueKind === 'boolean' ? wheel.value : toWheelValue(wheel, radix);
}

export function resolvePrimitiveOperationRod(args: {
    toolStep: Pick<MutableToolStep, 'stepKey' | 'toolHandle'>;
    preWorkflowState: MutableWorkflowState;
    postWorkflowState?: MutableWorkflowState;
    primitiveConfig: PrimitiveConfig;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): PrimitiveOperationRodResolution {
    const tool = args.toolMap.get(args.toolStep.toolHandle);
    const descriptor = resolveRodDescriptor(tool);
    if (descriptor === undefined) return { status: 'unsupported-tool' };

    const resourceTypeHandleByWorkflowRole = descriptor.resourceTypeHandleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        ResourceTypeHandle
    >>>;
    const inputByRole = new Map<PrimitiveOperationWorkflowRole, PrimitiveRodProjection>();
    for (const roleName of descriptor.inputRoles) {
        const entry = resolveInputEntry({
            workflowState: args.preWorkflowState,
            stepKey: args.toolStep.stepKey,
            roleName,
        });
        if (entry === null) return { status: 'unresolved-input', roleName };

        const resourceTypeHandle = resourceTypeHandleByWorkflowRole[roleName];
        if (resourceTypeHandle === undefined) {
            throw new Error(`Rod descriptor '${descriptor.toolHandle}' has no resource type for '${roleName}'.`);
        }
        const projection = toPrimitiveRodProjection(entry, resourceTypeHandle);
        if (projection === null) {
            return { status: 'invalid-projection', roleName, projection: entry.projection };
        }
        inputByRole.set(roleName, projection);
    }

    const firstInput = inputByRole.get(descriptor.inputRoles[0]);
    if (firstInput === undefined) throw new Error(`Rod descriptor '${descriptor.toolHandle}' did not resolve its input.`);
    const secondInput = descriptor.inputRoles.length === 2
        ? inputByRole.get(descriptor.inputRoles[1])
        : undefined;
    if (descriptor.inputRoles.length === 2 && secondInput === undefined) {
        throw new Error(`Primitive operation rod descriptor '${descriptor.toolHandle}' did not resolve both operands.`);
    }
    const input: PrimitiveOperationInput = descriptor.descriptorKind === 'identity'
        ? descriptor.operation === 'natural-identity'
            ? { operation: descriptor.operation, input: firstInput as number }
            : { operation: descriptor.operation, input: firstInput as boolean }
        : descriptor.descriptorKind === 'boolean-algebra'
            ? descriptor.operation === 'logical-not'
                ? { operation: descriptor.operation, input: firstInput as boolean }
                : { operation: descriptor.operation, operandOne: firstInput as boolean, operandTwo: secondInput as boolean }
        : descriptor.descriptorKind === 'natural-less-than'
            ? { operation: descriptor.operation, source: firstInput as number, target: secondInput as number }
            : {
            operation: descriptor.operation,
            operandOne: firstInput as number,
            operandTwo: secondInput as number,
        };
    let steps: readonly PrimitiveStepState[];
    try {
        steps = derivePrimitiveStepStatesFromOperationInput({
            config: args.primitiveConfig, input,
            directionCatalog: args.primitiveConfig.sceneDirectionCatalogs?.visuals ?? WORKFLOW_ROD_DIRECTION_CATALOG,
        });
    } catch (error) {
        return {
            status: 'invalid-operation',
            message: error instanceof Error ? error.message : String(error),
        };
    }
    if (input.operation === 'multiplication') {
        // Workflow inputs stay attached to their typed panels; only the native rod opens and closes.
        const position = toWheelPosition({ layout: toWheelLayout({
            config: args.primitiveConfig, operandSchedulingMode: 'grouped',
        }), role: 'multiplier' });
        steps = steps.filter(step => step.stepKey !== 'rod-extend' && step.stepKey !== 'rod-contract')
            .map(step => ({ ...step, keyframes: step.keyframes.map(keyframe => ({
                ...keyframe, wheels: keyframe.wheels.map(wheel => wheel.role === 'multiplier'
                    ? { ...wheel, position } : step.stepKey === 'initial' ? wheel : {
                        ...wheel, position: [wheel.position[0], wheel.position[1],
                            wheel.position[2] + args.primitiveConfig.base.display.wheel.wheelGap] as [number, number, number],
                    }),
            })) }));
    }
    if (descriptor.descriptorKind === 'natural-less-than'
        && args.primitiveConfig.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye') {
        steps = [steps[0], {
            ...rodStepMomentKey('eye-outbound'),
            label: 'Send Decision Eye',
            description: 'Send the unresolved output hub to the Natural output-slot and pause before scanning.',
            keyframes: steps[0].keyframes,
        }, ...steps.slice(1)];
    }
    if (descriptor.descriptorKind === 'boolean-algebra') {
        steps = [
            { ...steps[0], ...rodStepMomentKey('workflow-boolean-ready'), label: 'Ready', description: 'Assigned Boolean operands remain on the workflow alignment.' },
            { ...steps[0], ...rodStepMomentKey('workflow-boolean-enter'), label: 'Unfold Boolean Graph', description: 'Slide operands into a tangent-aligned row and open Result channels.' },
            ...steps.slice(1),
            { ...steps.at(-1)!, ...rodStepMomentKey('workflow-boolean-exit'), label: 'Refold Boolean Graph', description: 'Close Result channels and return operands to the workflow alignment.' },
        ];
    }
    const terminalWheels = toPrimitiveStepWheels(steps[steps.length - 1]);
    const resultByOutputRole: Partial<Record<PrimitiveOperationWorkflowRole, PrimitiveRodProjection>> = {};
    for (const binding of descriptor.outputBindings) {
        resultByOutputRole[binding.workflowRole] = toRodWheelValue(
            terminalWheels.find((wheel) => wheel.role === binding.wheelRole),
            args.primitiveConfig.natural.radix,
        );
    }

    if (args.postWorkflowState !== undefined) {
        for (const binding of descriptor.outputBindings) {
            const outputEntry = resolveOutputEntry({
                workflowState: args.postWorkflowState,
                stepKey: args.toolStep.stepKey,
                roleName: binding.workflowRole,
            });
            const resourceTypeHandle = resourceTypeHandleByWorkflowRole[binding.workflowRole];
            if (resourceTypeHandle === undefined) {
                throw new Error(`Rod descriptor '${descriptor.toolHandle}' has no resource type for '${binding.workflowRole}'.`);
            }
            const output = toPrimitiveRodProjection(outputEntry, resourceTypeHandle);
            if (outputEntry !== null && output === null) {
                return {
                    status: 'invalid-projection',
                    roleName: binding.workflowRole,
                    projection: outputEntry.projection,
                };
            }
            const result = resultByOutputRole[binding.workflowRole];
            if (result === undefined) {
                throw new Error(`Primitive operation descriptor '${descriptor.toolHandle}' has no result for '${binding.workflowRole}'.`);
            }
            if (output !== result) {
                return {
                    status: 'output-mismatch',
                    roleName: binding.workflowRole,
                    expected: result,
                    actual: output,
                };
            }
        }
    }

    return { status: 'ready', descriptor, input, steps, resultByOutputRole };
}

export function resolveReadyPrimitiveOperationReplay(args: {
    fromSnapshot: TraceSnapshot | undefined;
    toSnapshot: TraceSnapshot | undefined;
    primitiveConfig?: PrimitiveConfig;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
}): ReadyPrimitiveOperationReplay | null {
    if (!args.fromSnapshot || !args.toSnapshot) return null;
    const toolStep = selectActiveToolStepFromTraceSnapshot(args.toSnapshot);
    if (!toolStep) return null;
    const resolution = resolvePrimitiveOperationRod({
        toolStep,
        preWorkflowState: args.fromSnapshot.position.workflowState,
        postWorkflowState: args.toSnapshot.position.workflowState,
        primitiveConfig: args.primitiveConfig ?? PRIMITIVE_ROD_CONFIG,
        toolMap: args.toolMap,
    });
    return resolution.status === 'ready' ? { toolStep, resolution } : null;
}

function findEntityConstellation(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    direction: 'input' | 'output';
    resourceTypeHandle: ResourceTypeHandle;
}): EntityConstellation | undefined {
    return args.frame.constellations.find((constellation): constellation is EntityConstellation => {
        if (constellation.constellationKind !== 'entity') return false;
        const parsed = parseWorkflowStateEntryConstellationKey(constellation.constellationKey);
        return parsed?.stepKey === args.stepKey
            && parsed.direction === args.direction
            && parsed.resourceTypeHandle === args.resourceTypeHandle;
    });
}

type WorkflowStateEntryGeometry = {
    angle: number;
    ringCenter: [number, number, number];
    ringIntersection: [number, number, number];
    direction: [number, number, number];
    center: [number, number, number];
};

function toWorkflowStateEntryGeometry(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    direction: 'input' | 'output';
    roleName: PrimitiveOperationWorkflowRole;
    resourceTypeHandle: ResourceTypeHandle;
}): WorkflowStateEntryGeometry | null {
    const spokeConstellation = findEntityConstellation(args);
    if (!spokeConstellation) return null;
    const spokeState = args.frame.constellationParametricState?.entityConstellationByKey?.[
        spokeConstellation.constellationKey
    ];
    if (spokeState?.layout?.layoutKind !== 'spoke') return null;
    if (spokeState.layout.anchor.anchorKind !== 'parametric') return null;

    const spokeCollection = args.frame.itemCollections.find(
        (collection) => collection.itemCollectionKey === spokeConstellation.itemCollectionKey,
    );
    if (spokeCollection?.itemCollectionKind !== 'entity') return null;
    const itemIndex = spokeCollection.items.findIndex((item) => {
        const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
        return parsed?.stepKey === args.stepKey && parsed.roleName === args.roleName;
    });
    if (itemIndex < 0) return null;

    const toolStepConstellation = args.frame.constellations.find((constellation): constellation is EntityConstellation => (
        constellation.constellationKind === 'entity'
        && parseToolStepConstellationKey(constellation.constellationKey) !== null
    ));
    if (!toolStepConstellation) return null;
    const toolStepState = args.frame.constellationParametricState?.entityConstellationByKey?.[
        toolStepConstellation.constellationKey
    ];
    if (toolStepState?.layout?.layoutKind !== 'concentric-ring') return null;
    const toolStepCollection = args.frame.itemCollections.find(
        (collection) => collection.itemCollectionKey === toolStepConstellation.itemCollectionKey,
    );
    if (toolStepCollection?.itemCollectionKind !== 'entity') return null;
    const ringIndex = toolStepCollection.items.findIndex(
        (item) => item.itemKey === buildToolStepItemKey(args.stepKey),
    );
    if (ringIndex < 0) return null;

    const ringRadius = toolStepState.layout.ringRadii?.[ringIndex]
        ?? toolStepState.layout.startRadius + ringIndex * toolStepState.layout.ringGap;
    const anchor = spokeState.layout.anchor;
    const angle = anchor.anglePolicy.anglePolicyKind === 'explicit'
        ? anchor.anglePolicy.angle
        : 0;
    const radialOffset = anchor.radialOffset ?? 0;
    const originRadius = ringRadius + radialOffset;
    const direction = spokeState.layout.direction;
    const directionLength = Math.hypot(...direction);
    if (directionLength <= 1e-12) return null;
    const itemOffset = spokeState.layout.itemOffsets?.[itemIndex]
        ?? spokeState.layout.spacing * itemIndex;
    const normalizedDirection: [number, number, number] = [
        direction[0] / directionLength,
        direction[1] / directionLength,
        direction[2] / directionLength,
    ];
    const ringCenter: [number, number, number] = [0, anchor.yOffset ?? 0, 0];
    const ringIntersection: [number, number, number] = [
        Math.cos(angle) * ringRadius,
        anchor.yOffset ?? 0,
        Math.sin(angle) * ringRadius,
    ];
    return {
        angle,
        ringCenter,
        ringIntersection,
        direction: normalizedDirection,
        center: [
        Math.cos(angle) * originRadius + direction[0] / directionLength * itemOffset,
        (anchor.yOffset ?? 0) + direction[1] / directionLength * itemOffset,
        Math.sin(angle) * originRadius + direction[2] / directionLength * itemOffset,
        ],
    };
}

export function derivePlacement(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    descriptor: PrimitiveOperationRodDescriptor;
    decisionAtOutput?: boolean;
    primitiveConfig?: PrimitiveConfig;
}): PrimitiveRodPlacement | null {
    const positionByRole: PrimitiveRodPlacement['positionByRole'] = {};
    const directionByRole: NonNullable<PrimitiveRodPlacement['directionByRole']> = {};
    const wheelRoleByWorkflowRole = args.descriptor.wheelRoleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        PrimitiveWheelState['role']
    >>>;
    const resourceTypeHandleByWorkflowRole = args.descriptor.resourceTypeHandleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        ResourceTypeHandle
    >>>;
    for (const roleName of args.descriptor.inputRoles) {
        const wheelRole = wheelRoleByWorkflowRole[roleName];
        const resourceTypeHandle = resourceTypeHandleByWorkflowRole[roleName];
        if (resourceTypeHandle === undefined) return null;
        const geometry = toWorkflowStateEntryGeometry({
            ...args,
            direction: 'input',
            roleName,
            resourceTypeHandle,
        });
        if (!wheelRole || !geometry) return null;
        positionByRole[wheelRole] = geometry.center;
        directionByRole[wheelRole] = geometry.direction;
    }
    for (const binding of args.descriptor.outputBindings) {
        const resourceTypeHandle = resourceTypeHandleByWorkflowRole[binding.workflowRole];
        if (resourceTypeHandle === undefined) return null;
        const geometry = toWorkflowStateEntryGeometry({
            ...args,
            direction: 'output',
            roleName: binding.workflowRole,
            resourceTypeHandle,
        });
        if (!geometry) return null;
        positionByRole[binding.wheelRole] = geometry.center;
        directionByRole[binding.wheelRole] = geometry.direction;
    }
    if (args.descriptor.descriptorKind === 'natural-less-than') {
        const decisionTitlePosition = positionByRole['less-than-decision'];
        const source = positionByRole['less-than-source'];
        const target = positionByRole['less-than-target'];
        if (source === undefined || target === undefined) return null;
        if (args.decisionAtOutput) {
            const outputGeometry = toWorkflowStateEntryGeometry({
                ...args,
                direction: 'output',
                roleName: 'LessThanDecision',
                resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
            });
            if (outputGeometry === null) return null;
            return {
                axis: outputGeometry.direction,
                positionByRole: {
                    'less-than-decision': outputGeometry.center,
                },
                directionByRole: {
                    'less-than-decision': outputGeometry.direction,
                },
            };
        } else {
            positionByRole['less-than-decision'] = [
                2 * target[0] - source[0],
                2 * target[1] - source[1],
                2 * target[2] - source[2],
            ];
        }
        const axisVector: [number, number, number] = [
            source[0] - target[0],
            source[1] - target[1],
            source[2] - target[2],
        ];
        const axisLength = Math.hypot(...axisVector);
        if (axisLength <= 1e-12) return null;
        if (args.primitiveConfig?.natural.display.comparison.naturalLessThanPresentationMode === 'boolean-channel') {
            const placement: PrimitiveRodPlacement = {
                axis: axisVector.map((value) => value / axisLength) as [number, number, number],
                positionByRole,
                directionByRole,
            };
            const { axis, tangent, up } = toPrimitiveRodPlacementBasis(placement);
            const output = decisionTitlePosition;
            if (output === undefined) return null;
            const positions = toBooleanWheelPositions(args.primitiveConfig, 'logical-xor');
            const origin = new THREE.Vector3(...output).addScaledVector(axis, -positions.output[2]);
            for (const [role, position] of [
                ['less-than-source', positions.inputOne],
                ['less-than-target', positions.inputTwo],
                ['less-than-decision', positions.output],
            ] as const) {
                positionByRole[role] = origin.clone().addScaledVector(tangent, position[0])
                    .addScaledVector(up, position[1]).addScaledVector(axis, position[2]).toArray();
            }
            return { ...placement, origin: origin.toArray() };
        }
        return {
            axis: axisVector.map((value) => value / axisLength) as [number, number, number],
            positionByRole,
            directionByRole,
            ...(decisionTitlePosition === undefined ? {} : {
                titlePositionByRole: { 'less-than-decision': decisionTitlePosition },
            }),
        };
    }
    const outputWheelRole = wheelRoleByWorkflowRole[args.descriptor.axisOutputRole];
    const outputPosition = outputWheelRole ? positionByRole[outputWheelRole] : undefined;
    const firstInputRole = args.descriptor.inputRoles[0];
    const firstInputWheelRole = wheelRoleByWorkflowRole[firstInputRole];
    const firstInputPosition = firstInputWheelRole ? positionByRole[firstInputWheelRole] : undefined;
    if (!outputWheelRole || !outputPosition || !firstInputPosition) return null;
    const axisVector: [number, number, number] = [
        firstInputPosition[0] - outputPosition[0],
        firstInputPosition[1] - outputPosition[1],
        firstInputPosition[2] - outputPosition[2],
    ];
    const axisLength = Math.hypot(...axisVector);
    if (axisLength <= 1e-12) return null;
    const multiplier = positionByRole.multiplier;
    const multiplicand = positionByRole.multiplicand;
    if (multiplier && multiplicand) {
        // An internal rod station, never a WorkflowState resource or connector owner.
        positionByRole['remaining-repetitions'] = multiplier.map(
            (coordinate, index) => (coordinate + multiplicand[index]) / 2,
        ) as [number, number, number];
        const counterAxis = multiplier.map((coordinate, index) => coordinate - multiplicand[index]);
        const counterAxisLength = Math.hypot(...counterAxis);
        directionByRole['remaining-repetitions'] = counterAxisLength <= 1e-12
            ? axisVector.map((coordinate) => coordinate / axisLength) as [number, number, number]
            : counterAxis.map((coordinate) => coordinate / counterAxisLength) as [number, number, number];
    }
    return {
        axis: axisVector.map((value) => value / axisLength) as [number, number, number],
        positionByRole,
        directionByRole,
    };
}

export function resolvePlacedBooleanGraphConfig(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    descriptor: BooleanAlgebraDescriptor;
    primitiveConfig: PrimitiveConfig;
}): PrimitiveConfig {
    const workflowPlacement = derivePlacement(args);
    const outputGeometry = toWorkflowStateEntryGeometry({
        ...args, direction: 'output', roleName: 'Result',
        resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
    });
    if (workflowPlacement === null || outputGeometry === null) {
        throw new Error(`Boolean operation '${args.stepKey}' has no workflow placement.`);
    }
    return deriveBooleanGraphConfig({
        config: args.primitiveConfig, operation: args.descriptor.operation,
        workflowPlacement, ringIntersection: outputGeometry.ringIntersection,
    });
}

export function deriveOperationCameraBounds(args: {
    frame: SpaceDerivationFrame;
    step: MutableToolStep;
    config: Config;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    target: [number, number, number];
    workflowState: MutableWorkflowState;
}): { center: [number, number, number]; radius: number } | undefined {
    const descriptor = resolveRodDescriptor(args.toolMap.get(args.step.toolHandle));
    if (!descriptor) return undefined;
    let config = resolvePrimitiveRodConfig(args.config);
    const workflowPlacement = derivePlacement({ frame: args.frame, stepKey: args.step.stepKey, descriptor, primitiveConfig: config });
    if (!workflowPlacement) throw new Error(`Operation inspection has no placement for ${args.step.stepKey}.`);
    const scope = `camera:${args.step.stepKey}`;
    const wheels = buildDescriptorRoleWheels({
        descriptor, workflowState: args.workflowState, stepKey: args.step.stepKey, primitiveConfig: config,
    });
    let rod: SpaceDerivationFrame;
    if (descriptor.descriptorKind === 'boolean-algebra') {
        config = resolvePlacedBooleanGraphConfig({
            frame: args.frame, stepKey: args.step.stepKey, descriptor, primitiveConfig: config,
        });
        rod = buildBooleanGraphFrame({
            spaceKey: args.frame.spaceKey, scope, config, wheels, workflowPlacement,
            graphPlacement: deriveBooleanGraphPlacement(workflowPlacement, config),
            booleanRod: buildBooleanRodState({ config, operation: descriptor.operation, inputOne: false, inputTwo: false }),
        });
    } else {
        rod = buildPrimitiveRodFrame({ spaceKey: args.frame.spaceKey, scope, config, wheels, placement: workflowPlacement });
    }
    const bounds = deriveFrameBoundingSphere(rod);
    if (!bounds) throw new Error(`Operation inspection has no geometry for ${args.step.stepKey}.`);
    let radius = new THREE.Vector3(...bounds.center).distanceTo(new THREE.Vector3(...args.target)) + bounds.radius;
    if (descriptor.descriptorKind === 'natural-less-than') {
        if (config.natural.display.comparison.naturalLessThanPresentationMode === 'boolean-channel') {
            const operation = resolvePrimitiveOperationRod({
                toolStep: args.step, preWorkflowState: args.workflowState, primitiveConfig: config, toolMap: args.toolMap,
            });
            if (operation.status === 'ready') {
                const seen = new Set<string>();
                for (const keyframe of operation.steps.flatMap((step) => step.keyframes)) {
                    const key = JSON.stringify(keyframe.wheels.map((wheel) => wheel.position));
                    if (seen.has(key)) continue;
                    seen.add(key);
                    const envelope = deriveFrameBoundingSphere(buildPrimitiveRodFrame({
                        spaceKey: args.frame.spaceKey, scope, config,
                        wheels: keyframe.wheels, booleanRod: keyframe.booleanRod, placement: workflowPlacement,
                    }));
                    const sweepRadius = config.base.display.wheel.innerRadius
                        + Math.max(0, ...keyframe.wheels.flatMap((wheel) => wheel.powers))
                        * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
                        + config.base.display.wheel.ringWidth;
                    if (envelope) radius = Math.max(radius,
                        new THREE.Vector3(...envelope.center).distanceTo(new THREE.Vector3(...args.target))
                        + envelope.radius + sweepRadius);
                }
            }
        }
        const output = toWorkflowStateEntryGeometry({
            frame: args.frame, stepKey: args.step.stepKey, direction: 'output',
            roleName: 'LessThanDecision', resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
        });
        const source = toWorkflowStateEntryGeometry({
            frame: args.frame, stepKey: args.step.stepKey, direction: 'input',
            roleName: 'LessThanSource', resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
        });
        if (!source || !output) throw new Error(`LessThan inspection has no transport geometry for ${args.step.stepKey}.`);
        const center = new THREE.Vector3(...source.ringCenter);
        const orbitRadius = center.distanceTo(new THREE.Vector3(...source.ringIntersection));
        const wheelRadius = config.base.display.wheel.innerRadius + config.base.display.wheel.ringWidth;
        radius = Math.max(radius, center.distanceTo(new THREE.Vector3(...args.target)) + orbitRadius + wheelRadius,
            new THREE.Vector3(...output.center).distanceTo(new THREE.Vector3(...args.target)) + wheelRadius);
    }
    return { center: args.target, radius };
}

/**
 * Memo key of the step-independent plan, beyond the identities of config, toolMap and the WorkflowStates.
 * Undefined when the plan depends on more of the frame than the operation placement (Boolean graphs).
 */
export function toPrimitiveCameraPlanKey(args: PrimitiveCameraPlanInput): string | undefined {
    const descriptor = resolveRodDescriptor(args.toolMap.get(args.step.toolHandle));
    if (!descriptor || descriptor.descriptorKind === 'boolean-algebra') return undefined;
    const placement = derivePlacement({ frame: args.frame, stepKey: args.step.stepKey, descriptor });
    if (!placement) return undefined;
    return JSON.stringify([args.frame.spaceKey, args.step.stepKey, args.step.toolHandle, args.scope, args.workflowStateEntryGap, placement]);
}

/** Recomputes tagged transient wheels as `receiverCenter - direction * fraction * slotPitch`; stationary anchors are untouched. */
export function buildStandoffOverride(args: {
    placement: PrimitiveRodPlacement;
    config: Config;
    workflowStateEntryGap: number;
}): (wheel: PrimitiveWheelState) => PrimitiveWheelState {
    const nearFraction = args.config.adapterModeConfig.standoffNearFraction;
    const farFraction = args.config.adapterModeConfig.standoffFarFraction;
    if (!Number.isFinite(nearFraction) || nearFraction < 0) {
        throw new Error('Runner standoffNearFraction must be finite and non-negative.');
    }
    if (!Number.isFinite(farFraction) || farFraction < 0) {
        throw new Error('Runner standoffFarFraction must be finite and non-negative.');
    }
    const slotPitch = args.workflowStateEntryGap;
    return (wheel) => {
        const location = wheel.receiverLocation;
        if (location === undefined || location.locationKind === 'source') return wheel;
        const center = args.placement.positionByRole[location.receiverRole];
        const direction = args.placement.directionByRole?.[location.receiverRole];
        if (center === undefined || direction === undefined) return wheel;
        const fraction = {
            receiver: 0,
            'standoff-near-ante': nearFraction / 2,
            'standoff-near': nearFraction,
            'standoff-far-ante': (nearFraction + farFraction) / 2,
            'standoff-far': farFraction,
        }[location.locationKind];
        const offset = fraction * slotPitch;
        return {
            ...wheel,
            position: [
                center[0] - direction[0] * offset,
                center[1] - direction[1] * offset,
                center[2] - direction[2] * offset,
            ],
        };
    };
}

export function mergeRodFrame(base: SpaceDerivationFrame, rod: SpaceDerivationFrame): SpaceDerivationFrame {
    return {
        ...base,
        itemCollections: [...base.itemCollections, ...rod.itemCollections],
        constellations: [...base.constellations, ...rod.constellations],
        itemParametricState: {
            entityItemByKey: {
                ...(base.itemParametricState?.entityItemByKey ?? {}),
                ...(rod.itemParametricState?.entityItemByKey ?? {}),
            },
            connectorItemByKey: {
                ...(base.itemParametricState?.connectorItemByKey ?? {}),
                ...(rod.itemParametricState?.connectorItemByKey ?? {}),
            },
        },
        constellationParametricState: {
            entityConstellationByKey: {
                ...(base.constellationParametricState?.entityConstellationByKey ?? {}),
                ...(rod.constellationParametricState?.entityConstellationByKey ?? {}),
            },
            connectorConstellationByKey: {
                ...(base.constellationParametricState?.connectorConstellationByKey ?? {}),
                ...(rod.constellationParametricState?.connectorConstellationByKey ?? {}),
            },
        },
    };
}

function reanchorWorkflowStateEntryConnectors(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    descriptor: PrimitiveOperationRodDescriptor;
    traceHandle: string;
    roleKind: 'inputs' | 'outputs' | 'all';
}): SpaceDerivationFrame {
    const wheelRoleByWorkflowRole = args.descriptor.wheelRoleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        PrimitiveWheelState['role']
    >>>;
    const resourceTypeHandleByWorkflowRole = args.descriptor.resourceTypeHandleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        ResourceTypeHandle
    >>>;
    const inputRoles = args.roleKind === 'outputs' ? [] : args.descriptor.inputRoles.map((roleName) => ({
        roleName,
        direction: 'input' as const,
    }));
    const outputRoles = args.roleKind === 'inputs' ? [] : args.descriptor.outputBindings.map((binding) => ({
        roleName: binding.workflowRole,
        direction: 'output' as const,
    }));
    const wheelAnchorsByWorkflowStateEntryItemKey = new Map<string, Readonly<Record<'min' | 'max', ItemKey<'entity'>>>>();

    for (const { roleName, direction } of [...inputRoles, ...outputRoles]) {
        const wheelRole = wheelRoleByWorkflowRole[roleName];
        const resourceTypeHandle = resourceTypeHandleByWorkflowRole[roleName];
        if (wheelRole === undefined || resourceTypeHandle === undefined) continue;
        const workflowStateEntryItemKey = buildWorkflowStateEntryItemKey({
            stepKey: args.stepKey,
            direction,
            resourceTypeHandle,
            roleName,
        });
        const scope = `${args.traceHandle}:${args.stepKey}`;
        wheelAnchorsByWorkflowStateEntryItemKey.set(workflowStateEntryItemKey, {
            min: toScopedPrimitiveRodKey(
                toPrimitiveWheelConnectorAnchorItemKey({ wheelRole, side: 'min' }),
                scope,
            ),
            max: toScopedPrimitiveRodKey(
                toPrimitiveWheelConnectorAnchorItemKey({ wheelRole, side: 'max' }),
                scope,
            ),
        });
    }
    if (wheelAnchorsByWorkflowStateEntryItemKey.size === 0) return args.frame;

    const toAnchorPosition = (itemKey: string): [number, number, number] | null => {
        for (const constellation of args.frame.constellations) {
            if (constellation.constellationKind !== 'entity' || constellation.anchor.itemKey !== itemKey) continue;
            const layout = args.frame.constellationParametricState?.entityConstellationByKey?.[
                constellation.constellationKey
            ]?.layout;
            if (layout?.layoutKind === 'sector-ring' && layout.anchor.anchorKind === 'position') {
                return [...layout.anchor.position];
            }
        }
        const parsed = parseWorkflowStateEntryItemKey(itemKey);
        if (parsed === null) return null;
        return toWorkflowStateEntryGeometry({
            frame: args.frame,
            stepKey: parsed.stepKey,
            direction: parsed.direction,
            roleName: parsed.roleName as PrimitiveOperationWorkflowRole,
            resourceTypeHandle: parsed.resourceTypeHandle as ResourceTypeHandle,
        })?.center ?? null;
    };
    const toEndpointCandidates = (endpoint: ConnectorEndpointRef) => {
        if (endpoint.endpointKind !== 'static') return [];
        const wheelAnchors = wheelAnchorsByWorkflowStateEntryItemKey.get(endpoint.itemKey);
        const anchorKeys = wheelAnchors === undefined
            ? [endpoint.itemKey]
            : [wheelAnchors.min, wheelAnchors.max];
        return anchorKeys.flatMap((itemKey) => {
            const position = toAnchorPosition(itemKey);
            return position === null ? [] : [{ itemKey, position }];
        });
    };
    const toEndpointCandidatesForConnector = (endpoint: ConnectorEndpointRef, side: 'min' | 'max') => {
        if (endpoint.endpointKind !== 'static') return [];
        const wheelAnchors = wheelAnchorsByWorkflowStateEntryItemKey.get(endpoint.itemKey);
        if (wheelAnchors === undefined) {
            return toEndpointCandidates(endpoint);
        }
        const itemKey = wheelAnchors[side];
        const position = toAnchorPosition(itemKey);
        return position === null ? [] : [{ itemKey, position }];
    };

    const itemCollections = args.frame.itemCollections.map((collection) => {
        if (collection.itemCollectionKind !== 'connector') return collection;
        const items = collection.items.map((item) => {
            const fromEndpoint = item.endpoints.from;
            const toEndpoint = item.endpoints.to;
            if (fromEndpoint.endpointKind !== 'static' || toEndpoint.endpointKind !== 'static') return item;
            // Panel max/max denotes an input reference; placed wheel min is its high-angle rim.
            const endpointResolution = args.frame.itemParametricState?.connectorItemByKey?.[item.itemKey]?.endpointResolution;
            const side = endpointResolution?.fromSideVariant === 'max'
                && endpointResolution.toSideVariant === 'max' ? 'min' : 'max';
            const fromCandidates = toEndpointCandidatesForConnector(fromEndpoint, side);
            const toCandidates = toEndpointCandidatesForConnector(toEndpoint, side);
            if (fromCandidates.length === 0 || toCandidates.length === 0) return item;
            const reanchorsCurrentEndpoint = wheelAnchorsByWorkflowStateEntryItemKey.has(fromEndpoint.itemKey)
                || wheelAnchorsByWorkflowStateEntryItemKey.has(toEndpoint.itemKey);
            if (!reanchorsCurrentEndpoint) return item;
            const selectedFrom = fromCandidates[0];
            const selectedTo = toCandidates[0];
            if (selectedFrom === undefined || selectedTo === undefined) return item;
            const from = selectedFrom.itemKey === fromEndpoint.itemKey
                ? fromEndpoint
                : {
                    endpointKind: 'static' as const,
                    itemKey: selectedFrom.itemKey as ItemKey<'entity'>,
                    anchorKey: 'center' as const,
                };
            const to = selectedTo.itemKey === toEndpoint.itemKey
                ? toEndpoint
                : {
                    endpointKind: 'static' as const,
                    itemKey: selectedTo.itemKey as ItemKey<'entity'>,
                    anchorKey: 'center' as const,
                };
            return from === fromEndpoint && to === toEndpoint
                ? item
                : { ...item, endpoints: { from, to } };
        });
        return { ...collection, items };
    });

    return {
        ...args.frame,
        itemCollections,
        itemParametricState: {
            ...(args.frame.itemParametricState ?? {}),
        },
    };
}

export function hideWorkflowStateEntryProxies(args: {
    frame: SpaceDerivationFrame;
    stepKey: string;
    descriptor: PrimitiveOperationRodDescriptor;
    preserveOutputProxies?: boolean;
}): SpaceDerivationFrame {
    const hiddenByKey: Partial<Record<ItemKey<'entity'>, EntityItemParametricState>> = {};
    const roleNames = new Set<PrimitiveOperationWorkflowRole>([
        ...args.descriptor.inputRoles,
        ...(args.preserveOutputProxies
            ? []
            : args.descriptor.outputBindings.map((binding) => binding.workflowRole)),
    ]);
    for (const collection of args.frame.itemCollections) {
        if (collection.itemCollectionKind !== 'entity') continue;
        for (const item of collection.items) {
            const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
            if (parsed?.stepKey !== args.stepKey) continue;
            if (!roleNames.has(parsed.roleName as PrimitiveOperationWorkflowRole)) continue;
            const previous = args.frame.itemParametricState?.entityItemByKey?.[item.itemKey];
            hiddenByKey[item.itemKey] = {
                ...previous,
                visibility: { ...previous?.visibility, visible: false },
                visualStyle: { ...previous?.visualStyle, opacity: 0 },
                ...(previous?.label?.onItem === undefined ? {} : {
                    label: Object.fromEntries(
                        Object.entries(previous.label).filter(([channel]) => channel !== 'onItem'),
                    ),
                }),
            };
        }
    }
    return {
        ...args.frame,
        itemParametricState: {
            ...(args.frame.itemParametricState ?? {}),
            entityItemByKey: {
                ...(args.frame.itemParametricState?.entityItemByKey ?? {}),
                ...hiddenByKey,
            },
        },
    };
}

export function buildDescriptorRoleWheels(args: {
    descriptor: PrimitiveOperationRodDescriptor;
    workflowState: MutableWorkflowState;
    stepKey: MutableToolStep['stepKey'];
    primitiveConfig: PrimitiveConfig;
}): PrimitiveWheelState[] {
    const wheelRoleByWorkflowRole = args.descriptor.wheelRoleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        PrimitiveWheelState['role']
    >>>;
    const resourceTypeHandleByWorkflowRole = args.descriptor.resourceTypeHandleByWorkflowRole as Readonly<Partial<Record<
        PrimitiveOperationWorkflowRole,
        ResourceTypeHandle
    >>>;
    const inputRoles = new Set<PrimitiveOperationWorkflowRole>(args.descriptor.inputRoles);
    const layout = toWheelLayout({
        config: args.primitiveConfig,
        operandSchedulingMode: args.primitiveConfig.natural.operandSchedulingMode,
    });
    const roleNames = [
        ...args.descriptor.inputRoles,
        ...args.descriptor.outputBindings.map((binding) => binding.workflowRole),
    ];

    const wheels = roleNames.flatMap((roleName) => {
        const wheelRole = wheelRoleByWorkflowRole[roleName];
        const resourceTypeHandle = resourceTypeHandleByWorkflowRole[roleName];
        if (wheelRole === undefined || resourceTypeHandle === undefined) return [];
        const entry = inputRoles.has(roleName)
            ? resolveInputEntry({ workflowState: args.workflowState, stepKey: args.stepKey, roleName })
            : resolveOutputEntry({ workflowState: args.workflowState, stepKey: args.stepKey, roleName });
        const projection = toPrimitiveRodProjection(entry, resourceTypeHandle);

        if (resourceTypeHandle === CONSTANTS.Handles.ResourceTypes.TYPE_Boolean) {
            return [typeof projection === 'boolean'
                ? {
                    valueKind: 'boolean' as const,
                    value: projection,
                    role: wheelRole,
                    powers: [0],
                    position: toWheelPosition({ layout, role: wheelRole }),
                }
                : buildBooleanPlaceholderWheel({ layout, role: wheelRole })];
        }
        return [typeof projection === 'number'
            ? buildOperandWheel({ layout, role: wheelRole, value: projection })
            : buildNaturalPlaceholderWheel({ layout, role: wheelRole })];
    });
    if (args.descriptor.descriptorKind === 'boolean-algebra'
        && wheels.some((wheel) => wheel.role === 'boolean-output' && wheel.valueKind === 'boolean')) {
        return dimStableWheelRoles({
            config: args.primitiveConfig, wheels,
            roles: ['boolean-input-one', 'boolean-input-two'],
        });
    }
    return wheels;
}

export function augmentFrameWithPrimitiveOperationRods(args: {
    frame: SpaceDerivationFrame;
    traceSnapshot: TraceSnapshotView | undefined;
    pendingReplayMove?: PrimitiveOperationPendingReplayMove;
    pendingTargetSnapshot?: TraceSnapshot;
    config: Config;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    workflowStateEntryGap: number;
}): SpaceDerivationFrame {
    if (resolveOperationPresentationMode(args.config) !== 'wheels') return args.frame;
    if (!args.traceSnapshot) return applyWorkflowStateEntryConnectorVisibility({
        frame: args.frame, config: args.config, transformedBooleanStepKeys: new Set(),
    });

    const basePrimitiveConfig = resolvePrimitiveRodConfig(args.config);
    const traceSnapshot = args.traceSnapshot;
    let frame = args.frame;
    const transformedBooleanStepKeys = new Set<string>();
    for (const toolStep of traceSnapshot.position.orderedToolSteps) {
        const tool = args.toolMap.get(toolStep.toolHandle);
        const descriptor = resolveRodDescriptor(tool);
        if (!descriptor) continue;
        const primitiveConfig = descriptor.descriptorKind === 'boolean-algebra'
            ? resolvePlacedBooleanGraphConfig({
                frame, stepKey: toolStep.stepKey, descriptor, primitiveConfig: basePrimitiveConfig,
            })
            : basePrimitiveConfig;
        frame = hideWorkflowStateEntryProxies({
            frame,
            stepKey: toolStep.stepKey,
            descriptor,
        });
        const isPendingToolStep = args.pendingReplayMove !== undefined
            && selectActiveToolStepFromTraceSnapshot(args.pendingTargetSnapshot)?.stepKey === toolStep.stepKey;
        const hasAllCurrentOutputs = descriptor.outputBindings.every((binding) => resolveOutputEntry({
            workflowState: traceSnapshot.position.workflowState,
            stepKey: toolStep.stepKey,
            roleName: binding.workflowRole,
        }) !== null);
        const resolution = resolvePrimitiveOperationRod({
            toolStep,
            preWorkflowState: traceSnapshot.position.workflowState,
            ...(isPendingToolStep && args.pendingTargetSnapshot
                ? { postWorkflowState: args.pendingTargetSnapshot.position.workflowState }
                : !hasAllCurrentOutputs
                    ? {}
                    : { postWorkflowState: traceSnapshot.position.workflowState }),
            primitiveConfig,
            toolMap: args.toolMap,
        });
        if (descriptor.descriptorKind === 'boolean-algebra') {
            const workflowPlacement = derivePlacement({ frame, stepKey: toolStep.stepKey, descriptor });
            if (workflowPlacement === null) throw new Error(`Boolean operation '${toolStep.stepKey}' has no workflow placement.`);
            const scope = `${traceSnapshot.selectedTraceHandle}:${toolStep.stepKey}`;
            const committedIndex = isPendingToolStep ? args.pendingReplayMove?.arithmeticProgress.committedStepIndex ?? 0 : 0;
            const graphStep = resolution.status === 'ready' && committedIndex > 0
                ? resolution.steps[committedIndex] : undefined;
            const graphKeyframe = graphStep?.keyframes.at(-1);
            const graph = graphStep?.stepKey !== 'workflow-boolean-exit' && graphKeyframe?.booleanRod !== undefined;
            const rod = graph && graphKeyframe !== undefined && graphKeyframe.booleanRod !== undefined
                ? buildBooleanGraphFrame({
                    spaceKey: frame.spaceKey, config: primitiveConfig, scope,
                    wheels: graphKeyframe.wheels, booleanRod: graphKeyframe.booleanRod,
                    workflowPlacement,
                    graphPlacement: deriveBooleanGraphPlacement(workflowPlacement, primitiveConfig),
                })
                : buildPrimitiveRodFrame({
                    spaceKey: frame.spaceKey, config: primitiveConfig, scope, placement: workflowPlacement,
                    wheels: buildDescriptorRoleWheels({
                        descriptor, workflowState: traceSnapshot.position.workflowState,
                        stepKey: toolStep.stepKey, primitiveConfig,
                    }),
                });
            frame = mergeRodFrame(frame, rod);
            if (graph) {
                frame = replaceBooleanAlignment(frame, scope);
                transformedBooleanStepKeys.add(toolStep.stepKey);
            }
            frame = reanchorWorkflowStateEntryConnectors({
                frame, stepKey: toolStep.stepKey, descriptor,
                traceHandle: traceSnapshot.selectedTraceHandle, roleKind: 'all',
            });
            continue;
        }
        if (resolution.status !== 'ready') {
            const placement = derivePlacement({
                frame,
                stepKey: toolStep.stepKey,
                descriptor,
                primitiveConfig,
            });
            if (!placement) continue;
            const roleWheels = buildDescriptorRoleWheels({
                descriptor,
                workflowState: traceSnapshot.position.workflowState,
                stepKey: toolStep.stepKey,
                primitiveConfig,
            });
            const wheels = descriptor.descriptorKind === 'natural-less-than'
                ? roleWheels.filter((wheel) => wheel.role !== 'less-than-decision')
                : roleWheels;
            frame = mergeRodFrame(frame, buildPrimitiveRodFrame({
                spaceKey: frame.spaceKey,
                config: primitiveConfig,
                wheels,
                scope: `${args.traceSnapshot.selectedTraceHandle}:${toolStep.stepKey}`,
                placement,
                postPlacementOverride: buildStandoffOverride({ placement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap }),
            }));
            frame = reanchorWorkflowStateEntryConnectors({
                frame,
                stepKey: toolStep.stepKey,
                descriptor,
                traceHandle: args.traceSnapshot.selectedTraceHandle,
                roleKind: descriptor.descriptorKind === 'natural-less-than' ? 'inputs' : 'all',
            });
            if (descriptor.descriptorKind === 'natural-less-than') {
                const outputPlacement = derivePlacement({
                    frame,
                    stepKey: toolStep.stepKey,
                    descriptor,
                    decisionAtOutput: true,
                });
                const outputWheels = roleWheels.filter((wheel) => wheel.role === 'less-than-decision');
                if (outputPlacement !== null && outputWheels.length > 0) {
                    frame = mergeRodFrame(frame, buildPrimitiveRodFrame({
                        spaceKey: frame.spaceKey,
                        config: primitiveConfig,
                        wheels: outputWheels,
                        scope: `${args.traceSnapshot.selectedTraceHandle}:${toolStep.stepKey}`,
                        placement: outputPlacement,
                        postPlacementOverride: buildStandoffOverride({ placement: outputPlacement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap }),
                    }));
                    frame = reanchorWorkflowStateEntryConnectors({
                        frame,
                        stepKey: toolStep.stepKey,
                        descriptor,
                        traceHandle: args.traceSnapshot.selectedTraceHandle,
                        roleKind: 'outputs',
                    });
                }
            }
            continue;
        }
        const placement = derivePlacement({
            frame,
            stepKey: toolStep.stepKey,
            descriptor,
            primitiveConfig,
        });
        if (!placement) continue;
        const selectedStep = isPendingToolStep && args.pendingReplayMove
            ? resolution.steps[args.pendingReplayMove.arithmeticProgress.committedStepIndex]
            : !hasAllCurrentOutputs
                ? resolution.steps[0]
                : resolution.steps.at(-1);
        if (!selectedStep) continue;
        const selectedWheels = toPrimitiveStepWheels(selectedStep);
        const decisionAtOutput = primitiveConfig.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye'
            && (!isPendingToolStep || args.pendingReplayMove?.arithmeticProgress.committedStepIndex === 0);
        const wheels = descriptor.descriptorKind === 'natural-less-than' && decisionAtOutput
            ? selectedWheels.filter((wheel) => wheel.role !== 'less-than-decision')
            : selectedWheels;
        const rod = buildPrimitiveRodFrame({
            spaceKey: frame.spaceKey,
            config: primitiveConfig,
            wheels,
            ...(descriptor.descriptorKind === 'natural-less-than'
                && primitiveConfig.natural.display.comparison.naturalLessThanPresentationMode === 'boolean-channel'
                && selectedStep.keyframes.at(-1)?.booleanRod
                ? { booleanRod: selectedStep.keyframes.at(-1)!.booleanRod }
                : {}),
            scope: `${args.traceSnapshot.selectedTraceHandle}:${toolStep.stepKey}`,
            placement,
            postPlacementOverride: buildStandoffOverride({ placement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap }),
        });
        frame = mergeRodFrame(frame, rod);
        frame = reanchorWorkflowStateEntryConnectors({
            frame,
            stepKey: toolStep.stepKey,
            descriptor,
            traceHandle: args.traceSnapshot.selectedTraceHandle,
            roleKind: descriptor.descriptorKind === 'natural-less-than' ? 'inputs' : 'all',
        });
        if (descriptor.descriptorKind === 'natural-less-than'
            && primitiveConfig.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye') {
            const outputPlacement = derivePlacement({
                frame,
                stepKey: toolStep.stepKey,
                descriptor,
                decisionAtOutput: true,
            });
            const outputWheels = (isPendingToolStep
                ? toPrimitiveStepWheels(resolution.steps[0])
                : selectedWheels
            ).filter((wheel) => wheel.role === 'less-than-decision');
            if (decisionAtOutput && outputPlacement !== null && outputWheels.length > 0) {
                frame = mergeRodFrame(frame, buildPrimitiveRodFrame({
                    spaceKey: frame.spaceKey,
                    config: primitiveConfig,
                    wheels: outputWheels,
                    scope: `${args.traceSnapshot.selectedTraceHandle}:${toolStep.stepKey}`,
                    placement: outputPlacement,
                    postPlacementOverride: buildStandoffOverride({ placement: outputPlacement, config: args.config, workflowStateEntryGap: args.workflowStateEntryGap }),
                }));
                frame = reanchorWorkflowStateEntryConnectors({
                    frame,
                    stepKey: toolStep.stepKey,
                    descriptor,
                    traceHandle: args.traceSnapshot.selectedTraceHandle,
                    roleKind: 'outputs',
                });
            }
        }
    }
    return applyWorkflowStateEntryConnectorVisibility({ frame, config: args.config, transformedBooleanStepKeys });
}

export function removeScopedRod(frame: SpaceDerivationFrame, scope: string): SpaceDerivationFrame {
    const marker = `:primitive:${encodeURIComponent(scope)}:`;
    const omitScoped = <T>(record: Partial<Record<string, T>> | undefined) => Object.fromEntries(
        Object.entries(record ?? {}).filter(([key]) => !key.includes(marker)),
    );
    return {
        ...frame,
        itemCollections: frame.itemCollections.filter((collection) => !collection.itemCollectionKey.includes(marker)),
        constellations: frame.constellations.filter((constellation) => !constellation.constellationKey.includes(marker)),
        itemParametricState: {
            entityItemByKey: omitScoped(frame.itemParametricState?.entityItemByKey),
            connectorItemByKey: omitScoped(frame.itemParametricState?.connectorItemByKey),
        },
        constellationParametricState: {
            entityConstellationByKey: omitScoped(frame.constellationParametricState?.entityConstellationByKey),
            connectorConstellationByKey: omitScoped(frame.constellationParametricState?.connectorConstellationByKey),
        },
    };
}

export function replaceScopedRod(args: {
    base: SpaceDerivationFrame;
    rod: SpaceDerivationFrame;
    scope: string;
}): SpaceDerivationFrame {
    return mergeRodFrame(removeScopedRod(args.base, args.scope), args.rod);
}

function toShortestAngleDelta(from: number, to: number): number {
    const fullTurn = Math.PI * 2;
    return ((to - from + Math.PI) % fullTurn + fullTurn) % fullTurn - Math.PI;
}

function toLocalRotationDelta(
    from: readonly [number, number, number],
    to: readonly [number, number, number],
): { axis: [number, number, number]; angle: number } {
    const fromQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...from, 'XYZ'));
    const toQuaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(...to, 'XYZ'));
    const delta = fromQuaternion.clone().invert().multiply(toQuaternion).normalize();
    if (delta.w < 0) {
        delta.set(-delta.x, -delta.y, -delta.z, -delta.w);
    }
    const angle = 2 * Math.acos(THREE.MathUtils.clamp(delta.w, -1, 1));
    const scale = Math.sqrt(Math.max(0, 1 - delta.w * delta.w));
    return scale <= 1e-8
        ? { axis: [0, 1, 0], angle: 0 }
        : { axis: [delta.x / scale, delta.y / scale, delta.z / scale], angle };
}

function toDecisionRotation(args: {
    frame: SpaceDerivationFrame;
    wheels: readonly PrimitiveWheelState[];
    axis: readonly [number, number, number];
    position: readonly [number, number, number];
    primitiveConfig: PrimitiveConfig;
}): [number, number, number] | null {
    const decisionKey = toPrimitivePowerConstellationKey({
        wheelRole: 'less-than-decision',
        power: 0,
    });
    const rod = buildPrimitiveRodFrame({
        spaceKey: args.frame.spaceKey,
        config: args.primitiveConfig,
        wheels: args.wheels,
        placement: {
            axis: args.axis,
            positionByRole: { 'less-than-decision': args.position },
        },
    });
    const state = rod.constellationParametricState?.entityConstellationByKey?.[decisionKey];
    if (state?.layout?.layoutKind !== 'sector-ring') return null;
    return [...(state.layout.rotation ?? [0, 0, 0])] as [number, number, number];
}

export function deriveNaturalLessThanTransportPhases(args: {
    frame: SpaceDerivationFrame;
    settlementFrame: SpaceDerivationFrame;
    stepKey: string;
    scope: string;
    wheels: readonly PrimitiveWheelState[];
    config: Config;
    outbound?: boolean;
}): TransitionPhase[] {
    const primitiveConfig = resolvePrimitiveRodConfig(args.config);
    const sourceGeometry = toWorkflowStateEntryGeometry({
        frame: args.frame,
        stepKey: args.stepKey,
        direction: 'input',
        roleName: 'LessThanSource',
        resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
    });
    const outputGeometry = toWorkflowStateEntryGeometry({
        frame: args.frame,
        stepKey: args.stepKey,
        direction: 'output',
        roleName: 'LessThanDecision',
        resourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
    });
    if (sourceGeometry === null || outputGeometry === null) {
        throw new Error(`NaturalLessThan '${args.stepKey}' transport requires Natural input and Boolean output geometry.`);
    }

    const unscopedKey = toPrimitivePowerConstellationKey({
        wheelRole: 'less-than-decision',
        power: 0,
    });
    const decisionKey = unscopedKey.replace(
        ':primitive:',
        `:primitive:${encodeURIComponent(args.scope)}:`,
    ) as ConstellationKey<'entity'>;
    const hubKey = toScopedPrimitiveRodKey(toPrimitiveHubConstellationKey({ wheelRole: 'less-than-decision' }), args.scope);
    const movingKeys = [decisionKey, hubKey].filter((key) => args.frame.constellationParametricState?.entityConstellationByKey?.[key] !== undefined);
    const currentState = args.frame.constellationParametricState?.entityConstellationByKey?.[decisionKey];
    if (currentState?.layout?.layoutKind !== 'sector-ring') {
        throw new Error(`NaturalLessThan '${args.stepKey}' transport requires a rendered decision hub.`);
    }
    if (currentState.layout.anchor.anchorKind !== 'position') throw new Error('LessThan eye requires a position anchor.');
    const eyePosition = currentState.layout.anchor.position;
    const positionFor = (key: ConstellationKey<'entity'>, position: readonly [number, number, number]): [number, number, number] => {
        const anchor = args.frame.constellationParametricState?.entityConstellationByKey?.[key]?.layout?.anchor;
        if (anchor?.anchorKind !== 'position') throw new Error('LessThan transport requires position-anchored eye geometry.');
        return [position[0] + anchor.position[0] - eyePosition[0],
            position[1] + anchor.position[1] - eyePosition[1],
            position[2] + anchor.position[2] - eyePosition[2]];
    };
    const radialRotation = [...(currentState.layout.rotation ?? [0, 0, 0])] as [number, number, number];
    const naturalTangent: [number, number, number] = [
        -Math.sin(sourceGeometry.angle),
        0,
        Math.cos(sourceGeometry.angle),
    ];
    const booleanTangent: [number, number, number] = [
        -Math.sin(outputGeometry.angle),
        0,
        Math.cos(outputGeometry.angle),
    ];
    const naturalTangentRotation = toDecisionRotation({
        frame: args.frame,
        wheels: args.wheels,
        axis: naturalTangent,
        position: sourceGeometry.ringIntersection,
        primitiveConfig,
    });
    const booleanTangentRotation = toDecisionRotation({
        frame: args.frame,
        wheels: args.wheels,
        axis: booleanTangent,
        position: outputGeometry.ringIntersection,
        primitiveConfig,
    });
    const booleanRadialRotation = toDecisionRotation({
        frame: args.frame,
        wheels: args.wheels,
        axis: outputGeometry.direction,
        position: outputGeometry.ringIntersection,
        primitiveConfig,
    });
    if (naturalTangentRotation === null
        || booleanTangentRotation === null
        || booleanRadialRotation === null) {
        throw new Error(`NaturalLessThan '${args.stepKey}' transport could not resolve eye orientation.`);
    }

    const positionPatch = (position: readonly [number, number, number]): InterpolatedFramePatch => ({
        constellationParametricState: {
            entityConstellationByKey: {
                ...Object.fromEntries(movingKeys.map((key) => [key, { layout: { anchor: { position: positionFor(key, position) } } }])),
            },
        },
    });
    const rotationPatch = (
        from: readonly [number, number, number],
        to: readonly [number, number, number],
    ): InterpolatedFramePatch => ({
        constellationParametricState: {
            entityConstellationByKey: Object.fromEntries(movingKeys.map((key) => [key, { layout: { rotation: to } }])),
        },
        entityConstellationLocalRotationDeltaByKey: {
            ...Object.fromEntries(movingKeys.map((key) => [key, toLocalRotationDelta(from, to)])),
        },
    } as unknown as InterpolatedFramePatch);
    const orbitAngle = -toShortestAngleDelta(sourceGeometry.angle, outputGeometry.angle);
    const timing = args.config.adapterModeConfig.playback.naturalLessThanTransport;
    const orbitPatch = {
        constellationParametricState: {
            entityConstellationByKey: {
                ...Object.fromEntries(movingKeys.map((key) => [key, {
                    layout: {
                        anchor: { position: positionFor(key, outputGeometry.ringIntersection) },
                        rotation: booleanTangentRotation,
                    },
                }])),
            },
        },
        entityConstellationOrbitByKey: {
            ...Object.fromEntries(movingKeys.map((key) => [key, {
                center: sourceGeometry.ringCenter,
                axis: [0, 1, 0],
                angle: orbitAngle,
            }])),
        },
    } as unknown as InterpolatedFramePatch;

    if (args.outbound) {
        const localPosition = currentState.layout.anchor;
        if (localPosition.anchorKind !== 'position') throw new Error('LessThan eye requires a position anchor.');
        const phase = (key: string, patch: InterpolatedFramePatch, duration: number): TransitionPhase => ({
            patch, transitionOptions: { transitionKind: 'interpolated', transitionKey: `natural-less-than:${args.stepKey}:outbound:${key}`, duration },
        });
        const outboundOrbitPatch: InterpolatedFramePatch = {
            constellationParametricState: { entityConstellationByKey: Object.fromEntries(movingKeys.map((key) => [key, {
                layout: { anchor: { position: positionFor(key, sourceGeometry.ringIntersection) }, rotation: naturalTangentRotation },
            }])) },
            entityConstellationOrbitByKey: Object.fromEntries(movingKeys.map((key) => [key, {
                center: sourceGeometry.ringCenter, axis: [0, 1, 0], angle: -orbitAngle,
            }])),
        };
        const outputStart: SpaceDerivationFrame = {
            ...args.frame,
            constellationParametricState: {
                ...args.frame.constellationParametricState,
                entityConstellationByKey: {
                    ...args.frame.constellationParametricState?.entityConstellationByKey,
                    ...Object.fromEntries(movingKeys.map((key) => [key, {
                        ...args.frame.constellationParametricState?.entityConstellationByKey?.[key],
                        layout: {
                            ...args.frame.constellationParametricState?.entityConstellationByKey?.[key]?.layout,
                            anchor: { anchorKind: 'position' as const, position: positionFor(key, outputGeometry.center) },
                            rotation: booleanRadialRotation,
                        },
                    }])),
                },
            },
        };
        return [
            { patch: toPrimitiveGeometryFramePatch(outputStart), transitionOptions: { transitionKind: 'instant', transitionKey: `natural-less-than:${args.stepKey}:outbound:start` } },
            phase('boolean-radial', positionPatch(outputGeometry.ringIntersection), timing.booleanRadialExitDurationMs),
            phase('boolean-hinge', rotationPatch(booleanRadialRotation, booleanTangentRotation), timing.booleanRingHingeDurationMs),
            phase('tool-step-ring', outboundOrbitPatch, timing.toolStepRingTravelDurationMs),
            phase('natural-hinge', rotationPatch(naturalTangentRotation, radialRotation), timing.naturalRingHingeDurationMs),
            phase('natural-radial', positionPatch(localPosition.position), timing.naturalRadialEntryDurationMs),
            { patch: toPrimitiveGeometryFramePatch(args.frame), transitionOptions: { transitionKind: 'instant', transitionKey: `natural-less-than:${args.stepKey}:outbound:arrive` } },
        ];
    }
    return [
        {
            patch: toPrimitiveGeometryFramePatch(args.frame),
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `natural-less-than:${args.stepKey}:materialize-decision`,
            },
        },
        {
            patch: positionPatch(sourceGeometry.ringIntersection),
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: `natural-less-than:${args.stepKey}:radial-entry`,
                duration: timing.naturalRadialEntryDurationMs,
            },
        },
        {
            patch: rotationPatch(radialRotation, naturalTangentRotation),
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: `natural-less-than:${args.stepKey}:hinge-to-ring`,
                duration: timing.naturalRingHingeDurationMs,
            },
        },
        {
            patch: orbitPatch,
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: `natural-less-than:${args.stepKey}:tool-step-ring`,
                duration: timing.toolStepRingTravelDurationMs,
            },
        },
        {
            patch: rotationPatch(booleanTangentRotation, booleanRadialRotation),
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: `natural-less-than:${args.stepKey}:hinge-to-boolean`,
                duration: timing.booleanRingHingeDurationMs,
            },
        },
        {
            patch: positionPatch(outputGeometry.center),
            transitionOptions: {
                transitionKind: 'interpolated',
                transitionKey: `natural-less-than:${args.stepKey}:radial-exit`,
                duration: timing.booleanRadialExitDurationMs,
            },
        },
        {
            patch: toPrimitiveGeometryFramePatch(args.settlementFrame),
            transitionOptions: {
                transitionKind: 'instant',
                transitionKey: `natural-less-than:${args.stepKey}:settle`,
                delay: timing.settlementDurationMs,
            },
        },
    ];
}